import { useState } from "react";
import { Check, Palette, Layout, Sparkles } from "lucide-react";

export type ThemeLayout = "classic" | "modern" | "minimal";

interface ThemeSelectorProps {
  selectedTheme: ThemeLayout;
  onThemeSelect: (theme: ThemeLayout) => void;
  disabled?: boolean;
}

const THEME_OPTIONS = [
  {
    id: "classic" as ThemeLayout,
    name: "Classic",
    description: "ธีมแบบดั้งเดิมที่คุ้นเคย เหมาะกับทุกประเภทคอร์ส",
    icon: Layout,
    preview: {
      headerBg: "from-blue-600 to-pink-600",
      cardBg: "bg-white",
      accentColor: "text-blue-600",
      layout: "grid"
    },
    features: [
      "เลย์เอาต์แบบกริด",
      "สีสันสดใส",
      "เน้นภาพหน้าปก",
      "เหมาะกับคอร์สทั่วไป"
    ]
  },
  {
    id: "modern" as ThemeLayout,
    name: "Modern",
    description: "ธีมทันสมัย มินิมอล เน้นเนื้อหาและประสบการณ์ผู้ใช้",
    icon: Sparkles,
    preview: {
      headerBg: "from-slate-900 to-slate-700",
      cardBg: "bg-gradient-to-br from-white to-gray-50",
      accentColor: "text-blue-600",
      layout: "list"
    },
    features: [
      "ดีไซน์ทันสมัย",
      "โทนสีเรียบหรู",
      "เน้นอ่านง่าย",
      "เหมาะกับคอร์สธุรกิจ/เทคโนโลยี"
    ]
  },
  {
    id: "minimal" as ThemeLayout,
    name: "Minimal",
    description: "ธีมมินิมัล สะอาดตา เน้นเนื้อหาเป็นหลัก",
    icon: Palette,
    preview: {
      headerBg: "from-gray-50 to-white",
      cardBg: "bg-white border-2 border-gray-200",
      accentColor: "text-gray-900",
      layout: "clean"
    },
    features: [
      "ดีไซน์เรียบง่าย",
      "พื้นที่ว่างเยอะ",
      "เน้นความชัดเจน",
      "เหมาะกับคอร์สศิลปะ/การออกแบบ"
    ]
  }
];

export default function ThemeSelector({ selectedTheme, onThemeSelect, disabled = false }: ThemeSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
          <Palette className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">เลือกธีมเว็บไซต์</h3>
          <p className="text-sm text-gray-600">เลือกรูปแบบที่เหมาะกับคอร์สของคุณ</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {THEME_OPTIONS.map((theme) => {
          const Icon = theme.icon;
          const isSelected = selectedTheme === theme.id;
          
          return (
            <button
              key={theme.id}
              onClick={() => !disabled && onThemeSelect(theme.id)}
              disabled={disabled}
              className={`relative text-left transition-all duration-300 rounded-2xl overflow-hidden ${
                isSelected
                  ? "ring-4 ring-blue-500 shadow-2xl scale-105"
                  : "hover:shadow-xl hover:scale-102 shadow-lg"
              } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {/* Selection Badge */}
              {isSelected && (
                <div className="absolute top-4 right-4 z-10 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-5 h-5 text-white" />
                </div>
              )}

              {/* Theme Preview */}
              <div className={`h-32 bg-gradient-to-br ${theme.preview.headerBg} p-4 flex items-center justify-center`}>
                <Icon className={`w-12 h-12 ${theme.id === 'minimal' ? 'text-gray-900' : 'text-white'} opacity-80`} />
              </div>

              {/* Theme Info */}
              <div className="bg-white p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  {theme.name}
                  {isSelected && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                      เลือกแล้ว
                    </span>
                  )}
                </h4>
                <p className="text-sm text-gray-600 mb-4">{theme.description}</p>

                {/* Features */}
                <ul className="space-y-2">
                  {theme.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                      <Check className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sample Layout Preview */}
              <div className="bg-gray-50 p-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2 font-semibold">ตัวอย่างเลย์เอาต์:</p>
                <div className="space-y-2">
                  {theme.preview.layout === "grid" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-8 bg-gray-300 rounded"></div>
                      <div className="h-8 bg-gray-300 rounded"></div>
                      <div className="h-8 bg-gray-300 rounded"></div>
                      <div className="h-8 bg-gray-300 rounded"></div>
                    </div>
                  )}
                  {theme.preview.layout === "list" && (
                    <div className="space-y-2">
                      <div className="h-6 bg-gray-300 rounded"></div>
                      <div className="h-6 bg-gray-300 rounded"></div>
                      <div className="h-6 bg-gray-300 rounded"></div>
                    </div>
                  )}
                  {theme.preview.layout === "clean" && (
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Info Notice */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">💡 คุณสามารถเปลี่ยนธีมได้ทุกเมื่อ</p>
          <p className="text-blue-700">
            ไปที่หน้า <strong>ตั้งค่า</strong> เพื่อเปลี่ยนธีมเว็บไซต์ของคุณได้ตามต้องการ
          </p>
        </div>
      </div>
    </div>
  );
}
